import pluralize from "pluralize";
import { Field, Module } from "../../types/TenantInterfaces";
import { useMemo } from "react";
import { useAtomValue } from "jotai";
import { modulesAtom, tenantIDAtom } from "../../atoms/GlobalAtoms";
import ItemCardList from "../../components/Modules/ItemCardList";
import SummaryCard from "../../components/Modules/SummaryCard";
import AppPageHeader from "../../components/Headers/AppPageHeader";
import AppEmptyState from "../../elements/AppEmptyState";

export default function ReferencedBy({
  currentModule,
}: {
  currentModule: Module;
}) {
  const tenantID = useAtomValue(tenantIDAtom);
  const modules = useAtomValue(modulesAtom);
  const moduleName = currentModule?.Name || "";
  const modAPIName = currentModule?.APIName;

  const referenceModules = useMemo(() => {
    if (!modules || !modAPIName) return {};

    return GetReferenceModules(modules, modAPIName);
  }, [modules, modAPIName]);

  // Empty State
  if (!Object.keys(referenceModules).length) {
    return (
      <>
        <AppPageHeader
          title="Referenced By"
          description="View data relationships for this data source"
        />
        <AppEmptyState
          title="Nothing to show"
          description={`There are no data sources that reference ${moduleName}`}
          hideIcon
        />
      </>
    );
  }

  return (
    <>
      <AppPageHeader
        title="Referenced By"
        description="View data relationships for this data source"
      />
      <div className="border shadow-sm p-3">
        <h3 className="text-dark mb-0">Source Name: {moduleName}</h3>
        <p className="m-0 p-0 text-sm">
          Below are all the data sources that reference{" "}
          <span className="text-dark font-weight-bold">{moduleName}</span>
        </p>

        <h4 className="text-dark mt-4">Referenced By:</h4>
        {Object.keys(referenceModules).map((key) => {
          const data = referenceModules[key];

          if (!data) return null;

          const { name, fields } = data;

          return (
            <ItemCardList
              key={key}
              summary
              item={{
                Name: name,
                FieldType: `${pluralize(moduleName)} (${moduleName} ID)`,
                Description: `Contains the fields that reference ${moduleName}`,
              }}
              nameLink={`/clouds/${tenantID}/settings/modules/manage/${key}/fields`}
            >
              {fields.map((f) => {
                return (
                  <ItemCardList key={f.APIName} summary item={f}>
                    <SummaryCard field={f} />
                  </ItemCardList>
                );
              })}
            </ItemCardList>
          );
        })}
      </div>
    </>
  );
}

type ModData = { [key: string]: { fields: Field[]; name: string } };

function GetReferenceModules(modules: Module[], modAPIName: string) {
  const modData: ModData = {};

  for (let i = 0; i < modules.length; i++) {
    const mod = modules[i];

    if (!mod?.Fields?.length || !mod.APIName) continue;

    CheckFieldsForReferences(mod.Fields, modAPIName, modData, mod);
  }

  return modData;
}

function CheckFieldsForReferences(
  fields: Field[],
  modAPIName: string,
  modData: ModData,
  mod: Module,
  parentField?: Field
) {
  for (let j = 0; j < fields.length; j++) {
    const field = fields[j];

    if (!field) continue;

    if (["Subitem", "MultiSubitem"].includes(field.FieldType)) {
      if (!field.SubitemFields) continue;

      CheckFieldsForReferences(
        field.SubitemFields,
        modAPIName,
        modData,
        mod,
        field
      );
    }

    if (["SingleReference", "MultiReference"].includes(field.FieldType)) {
      const sources = Array.isArray(field?.SourcesList)
        ? field?.SourcesList
        : [field?.SourcesList ?? ""];

      if (!sources.length) continue;

      for (const source of sources) {
        if (!source) continue;

        const splitSource = source.split(".");

        if (splitSource.length !== 3) continue;

        if (splitSource[2] === modAPIName) {
          if (modData[mod.APIName]) {
            if (!parentField) {
              modData[mod.APIName].fields.push(field);
            } else {
              modData[mod.APIName].fields.push({
                ...field,
                Name: `${parentField.Name}'s ${field.Name}`,
                APIName: `${parentField.APIName}.${field.APIName}`,
                FieldType: `${parentField.FieldType} - ${field.FieldType}`,
              });
            }
          } else {
            if (!parentField) {
              modData[mod.APIName] = {
                name: mod.Name,
                fields: [field],
              };
            } else {
              modData[mod.APIName] = {
                name: mod.Name,
                fields: [
                  {
                    ...field,
                    Name: `${pluralize(parentField.Name)} ${field.Name}`,
                    APIName: `${parentField.APIName}.${field.APIName}`,
                    FieldType: `${parentField.FieldType} - ${field.FieldType}`,
                  },
                ],
              };
            }
          }
        }
      }
    }
  }
}
